// src/mappers/reportMappers.ts
import { CompoundDetailsWithRelatedDataDomain } from 'compoundTypes'
import { Evidence, Relation, ResearchType } from 'evidenceTypes'
import { TestResult, TestType } from 'mtcTypes'
import {
    ReportDataById,
    Compound,
    Target,
    Driver,
    DataDomainItems,
    MappedDriver,
    MappedTarget,
    HeaderDataById
} from 'reportViewer'
import {
    isMappedCompound,
    isMappedDriver,
    isMappedTarget
} from 'utils/reportViewerUtils'

/** helper to extract unique fields from an array of Evidences */
type FieldToReturn = {
    researchType: ResearchType
    tumor: string
    histology: string
}

export function collectFieldsFromEvidences<K extends keyof FieldToReturn>(
    evidences: Evidence[],
    field: K
): FieldToReturn[K][] {
    // pull out raw values
    const raw = evidences
        .flatMap((e) => e.relations.map((r) => r[field]))
        .filter(
            (v): v is Exclude<Relation[K], null | undefined> =>
                v != null && v !== ''
        )

    // dedupe
    const unique = Array.from(new Set(raw))

    return unique ?? []
}

export const mapToMappedCompounds = (
    compounds: Compound[]
): CompoundDetailsWithRelatedDataDomain[] =>
    compounds.map((compound, idx) => {
        const targets = (compound.relatedTargets ?? []).map((rt) => ({
            name: rt.target.name ?? '',
            level: rt.level
        }))

        const drivers = (compound.relatedDrivers ?? []).map((rd) => {
            const g = rd.item?.gene?.name
            const m = rd.item?.mutation?.name
            return {
                name: g && m ? `${g}-${m}` : g || m || '',
                level: rd.level
            }
        })

        return {
            compoundId: compound.compound.id,
            name: compound.compound.name ?? '',
            level: compound.level,
            category: compound.category,
            evidences: compound.evidences ?? [],
            targets,
            drivers,
            linkPath: idx
        }
    })

export const mapToMappedDrivers = (
    molecularBiomarkers: Driver[]
): MappedDriver[] =>
    molecularBiomarkers.map((biomarker) => {
        const {
            item,
            level,
            driverClassification,
            testType,
            testResult,
            evidences
        } = biomarker
        const g = item?.gene?.name
        const m = item?.mutation?.name

        const idParts = [g, level, driverClassification, testType, testResult]
            .filter(Boolean)
            .join('-')

        return {
            id: idParts,
            item: {
                gene: { id: item?.gene?.id ?? '', name: g ?? '' },
                mutation: { id: item?.mutation?.id ?? '', name: m ?? '' },
                testType,
                testResult
            },
            level,
            driverClassification,
            testType,
            testResult,
            evidences
        }
    })

export const mapToMappedTargets = (targets: Target[]): MappedTarget[] =>
    targets.map((target) => {
        const relatedDrivers = (target.relatedDrivers ?? []).map((driver) => {
            const g = driver.item?.gene?.name
            const m = driver.item?.mutation?.name
            const idParts = [
                g,
                driver.level,
                driver.driverClassification,
                driver.testType,
                driver.testResult
            ]
                .filter(Boolean)
                .join('-')

            return {
                id: idParts,
                item: {
                    gene: { id: driver.item?.gene?.id ?? '', name: g ?? '' },
                    mutation: {
                        id: driver.item?.mutation?.id ?? '',
                        name: m ?? ''
                    },
                    testType: driver.testType,
                    testResult: driver.testResult
                },
                level: driver.level,
                driverClassification: driver.driverClassification,
                testType: driver.testType,
                testResult: driver.testResult,
                evidences: driver.evidences
            }
        })

        const researchType = collectFieldsFromEvidences(
            target.evidences ?? [],
            'researchType'
        )
        const tumors = collectFieldsFromEvidences(target.evidences ?? [], 'tumor')
        const histologies = collectFieldsFromEvidences(
            target.evidences ?? [],
            'histology'
        )

        return {
            id: target.target.id,
            item: { gene: target.target },
            level: target.level,
            relatedDrivers,
            evidences: target.evidences,
            testType: target.testType,
            testResult: target.testResult,
            researchType,
            tumors,
            histologies
        }
    })

export function processData(data: DataDomainItems): ReportDataById {
    return data.reduce<ReportDataById>((acc, entry, idx) => {
        acc[idx] = {
            id: String(idx),
            evidence: entry.evidences,
            level: entry.level
        }

        return acc
    }, {})
}

export function processHeaderData(data: DataDomainItems): HeaderDataById {
    return data.reduce<HeaderDataById>((acc, entry, idx) => {
        let name = 'N/A'
        let testType: TestType | null = null
        let testResult: TestResult | null = null

        if (isMappedCompound(entry)) {
            name = entry.name ?? 'Unnamed compound'
        } else if (isMappedDriver(entry)) {
            const g = entry.item?.gene?.name
            const m = entry.item?.mutation?.name
            name = g && m ? `${g}-${m}` : g || m || 'Unnamed driver'
            testType = entry.testType ?? null
            testResult = entry.testResult ?? null
        } else if (isMappedTarget(entry)) {
            name = entry.item?.gene?.name ?? 'Unnamed target'
            testType = entry.testType ?? null
            testResult = entry.testResult ?? null
        }

        acc[String(idx)] = {
            id: String(idx),
            item: {
                items: [
                    {
                        name,
                        testType,
                        testResult
                    }
                ]
            },
            level:
                entry.level !== undefined && entry.level !== null
                    ? entry.level
                    : null
        }

        return acc
    }, {})
}
